import { ButtonLoading } from "@/components/button-loading";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AdminTeamDTO } from "@shaarp/common";
import { useState } from "react";

export const DialogTeamCreation = ({
  open,
  onClose = () => {},
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const [loading, setLoading] = useState(false);
  const [managerEmail, setManagerEmail] = useState("");

  const createNewTeam = async () => {
    if (managerEmail === "") {
      toastError();
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post<AdminTeamDTO>(`/my/user/advisor/invite`, {
        managerEmail,
      });

      toast({
        variant: "default",
        title: t("team.creation.toast.success.title"),
        description: t("team.creation.toast.success.description", {
          name: data.name,
        }),
      });

      onClose();
    } catch (error) {
      toastError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="w-full max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t("team.creation.title")}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t("team.creation.description")}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("team.creation.fields.managerEmail.label")}
              </Label>
              <Input
                id="managerEmail"
                placeholder={t("team.creation.fields.managerEmail.placeholder")}
                type="email"
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            className={cn(isMobile ? "mt-2" : "")}
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t("team.creation.actions.cancel")}
          </Button>
          <ButtonLoading onClick={createNewTeam} isLoading={loading}>
            {t("team.creation.actions.send")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
